import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import AdminDashboard from "@/pages/dashboard/admin";
import ChapterHeadDashboard from "@/pages/dashboard/chapter-head";
import HaafCollectorDashboard from "@/pages/dashboard/haaf-collector";
import HaafCoordinatorDashboard from "@/pages/dashboard/haaf-coordinator";
import { useAuth } from "@/contexts/AuthContext";
import AnalyticsPage from "./pages/AnalyticsPage";
import PropTypes from "prop-types";
import UnauthorizedPage from "@/pages/UnauthorizedPage";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public Routes - No authentication required */}
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Auth Routes - Redirect if already logged in */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />

            {/* Protected Routes - Require authentication */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chapter/*"
              element={
                <ProtectedRoute allowedRoles={["chapter_head"]}>
                  <ChapterHeadDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/haaf-collector/*"
              element={
                <ProtectedRoute allowedRoles={["haaf-collector"]}>
                  <HaafCollectorDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/haaf-coordinator/*"
              element={
                <ProtectedRoute allowedRoles={["haaf-coordinator"]}>
                  <HaafCoordinatorDashboard />
                </ProtectedRoute>
              }
            />

            {/* Home route redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Catch-all route */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <RoleBasedRedirect />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Add PublicRoute component to prevent authenticated users from accessing login/register
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (user) {
    // Redirect to the page they came from, or their role-based dashboard
    const from =
      location.state?.from?.pathname ||
      (user.role === "admin"
        ? "/admin/overview"
        : user.role === "haaf-collector"
          ? "/haaf-collector/overview"
          : user.role === "haaf-coordinator"
            ? "/haaf-coordinator/overview"
            : "/chapter/overview");
    return <Navigate to={from} replace />;
  }

  return children;
};

PublicRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

function RoleBasedRedirect() {
  const { user } = useAuth();

  if (user?.role === "admin") {
    return <Navigate to="/admin/overview" replace />;
  }

  if (user?.role === "haaf-collector") {
    return <Navigate to="/haaf-collector/overview" replace />;
  }

  if (user?.role === "haaf-coordinator") {
    return <Navigate to="/haaf-coordinator/overview" replace />;
  }

  return <Navigate to="/chapter/overview" replace />;
}

export default App;
